import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { createContext, useCallback, useContext, useState } from "react";
import * as React from "react";

import { useAppDispatch } from "app/hooks";
import {
  resetConfirmDialog,
  setOpen as setConfirmChangeDialogOpen,
  setContent,
  setOnChange,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { APPOINTMENT_TYPE_GENERAL_TYPE_FITTING } from "constants/appointment-types";
import {
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_LOSS_DAMAGE_REQUESTED,
  PATIENT_PRODUCT_STATUS_ON_LOAN
} from "constants/patient-products";
import { useLazyGetPatientProductsQuery } from "services/patient-products";
import { Appointment } from "services/types/appointments.type";

import { MarkProductsAsDeliveredModal } from "../features/patients/sections/patient-hearing-aids/components/hearing-aid-requests-dialog/mark-products-as-delivered/mark-products-as-delivered-modal.component";

interface UndeliveredDevicesContextType {
  checkUndeliveredDevices: (
    appointment: Appointment | AppointmentModel
  ) => void;
}

const UndeliveredDevicesContext =
  createContext<UndeliveredDevicesContextType | null>(null);

export const UndeliveredDevicesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [currentAppointment, setCurrentAppointment] = useState<
    Appointment | AppointmentModel | null
  >(null);

  const [getPatientProducts] = useLazyGetPatientProductsQuery();

  const checkUndeliveredDevices = useCallback(
    async (appointment: Appointment | AppointmentModel) => {
      const isFittingAppointment =
        appointment?.appointmentType?.general_type ===
        Number(APPOINTMENT_TYPE_GENERAL_TYPE_FITTING);

      if (!isFittingAppointment || !appointment.patient?.id) {
        return;
      }

      // is_active: true matches the modal's own warranty-preview query below —
      // without it, a patient with historical/inactive PatientProduct rows
      // (RETURNED, EXCHANGED, etc. — all "not DELIVERED") can trip this
      // confirmation for devices that were never really delivery-eligible,
      // while the modal's filtered preview correctly finds nothing to show.
      const { data: patientProducts = [] } = await getPatientProducts({
        patientId: Number(appointment.patient.id),
        is_active: true,
        limit: 100,
        query: "{id,status}"
      });

      // A device on an open Loss & Damage claim (SN-7365) is with the patient
      // in every sense that matters here: it is not awaiting delivery, and
      // bulk-deliver refuses it server-side, so prompting would only produce
      // a modal with nothing to deliver.
      const hasUndeliveredDevices = patientProducts.some((product: any) => {
        return (
          product.status !== PATIENT_PRODUCT_STATUS_DELIVERED &&
          product.status !== PATIENT_PRODUCT_STATUS_ON_LOAN &&
          product.status !== PATIENT_PRODUCT_STATUS_LOSS_DAMAGE_REQUESTED
        );
      });

      if (hasUndeliveredDevices) {
        dispatch(setTitle("This Patient has Undelivered Devices"));
        dispatch(setContent("Do you want to mark them all as delivered?"));
        dispatch(
          setOnChange(() => {
            setCurrentAppointment(appointment);
            dispatch(resetConfirmDialog());
          })
        );
        dispatch(setConfirmChangeDialogOpen(true));
      }
    },
    [getPatientProducts, dispatch]
  );

  const handleModalClose = useCallback(() => {
    setCurrentAppointment(null);
  }, []);

  return (
    <UndeliveredDevicesContext.Provider value={{ checkUndeliveredDevices }}>
      {children}

      {currentAppointment && (
        <MarkProductsAsDeliveredModal
          isOpen={true}
          onCancel={handleModalClose}
          patientId={currentAppointment.patient.id}
          defaultAppointmentId={Number(currentAppointment.id)}
        />
      )}
    </UndeliveredDevicesContext.Provider>
  );
};

export const useUndeliveredDevicesCheck = () => {
  const context = useContext(UndeliveredDevicesContext);
  if (!context) {
    throw new Error(
      "useUndeliveredDevicesCheck must be used within a UndeliveredDevicesProvider"
    );
  }
  return context;
};
